import { useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  AD_PERSONALIZATION_CONSENT_STORAGE_KEY,
  DATA_DISCLOSURE_CONSENT_STORAGE_KEY,
  LEGAL_CONSENT_DONE_STORAGE_KEY,
  ONBOARDING_DONE_STORAGE_KEY
} from '../constants';
import {
  isAdConsentProviderAvailable,
  openAdPrivacyOptionsForm,
  refreshAdConsentProviderInfo,
  showAdConsentFormIfRequired
} from '../lib/adConsent';
import { appendMonetizationEvent } from '../lib/monetizationEvents';
import { nowMs } from '../lib/time';
import type { AppSettings } from '../types';

type SetState<T> = Dispatch<SetStateAction<T>>;

type UseConsentControllerParams = {
  adConsentStatus: 'unknown' | 'accepted' | 'rejected' | 'not_required';
  adConsentProviderAvailable: boolean;
  showOnboarding: boolean;
  pendingPersonalizedAds: boolean;
  settings: AppSettings;
  tr: (key: string, vars?: Record<string, string | number>) => string;
  setSettings: SetState<AppSettings>;
  setShowOnboarding: SetState<boolean>;
  setShowLegalConsent: SetState<boolean>;
  setShowDataDisclosure: SetState<boolean>;
  setShowAdConsent: SetState<boolean>;
  setPendingPersonalizedAds: SetState<boolean>;
  setAdConsentStatus: SetState<'unknown' | 'accepted' | 'rejected' | 'not_required'>;
  setAdConsentProviderAvailable: SetState<boolean>;
  setAdPrivacyOptionsAvailable: SetState<boolean>;
  setLegalDoc: SetState<'privacy' | 'terms' | null>;
  setOnboardingStep: SetState<number>;
  setBillingStatusText: SetState<string | null>;
};

export function useConsentController({
  adConsentStatus,
  adConsentProviderAvailable,
  showOnboarding,
  pendingPersonalizedAds,
  settings,
  tr,
  setSettings,
  setShowOnboarding,
  setShowLegalConsent,
  setShowDataDisclosure,
  setShowAdConsent,
  setPendingPersonalizedAds,
  setAdConsentStatus,
  setAdConsentProviderAvailable,
  setAdPrivacyOptionsAvailable,
  setLegalDoc,
  setOnboardingStep,
  setBillingStatusText
}: UseConsentControllerParams) {
  useEffect(() => {
    void (async () => {
      const available = await isAdConsentProviderAvailable();
      setAdConsentProviderAvailable(available);
      if (!available) return;
      try {
        const info = await refreshAdConsentProviderInfo();
        if (!info) return;
        setAdPrivacyOptionsAvailable(info.privacyOptionsRequired);
        if (info.status === 'accepted' || info.status === 'not_required') {
          setAdConsentStatus(info.status);
          return;
        }
        if (info.isRequired && adConsentStatus !== 'rejected') {
          setAdConsentStatus('unknown');
        }
      } catch {
        // Keep last known consent state.
      }
    })();
    // initial sync only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function isAdConsentRequired(): boolean {
    return adConsentStatus !== 'not_required';
  }

  async function updateAdPersonalization(enabled: boolean): Promise<void> {
    if (!enabled) {
      setPendingPersonalizedAds(false);
      if (isAdConsentRequired()) setAdConsentStatus('rejected');
      setSettings((prev) => ({ ...prev, adPersonalization: false }));
      appendMonetizationEvent({ category: 'consent', action: 'personalized_ads_disabled', status: 'info', detail: 'User disabled personalized ads.' });
      return;
    }
    if (adConsentProviderAvailable) {
      try {
        const info = await showAdConsentFormIfRequired();
        if (info) {
          setAdPrivacyOptionsAvailable(info.privacyOptionsRequired);
          if (info.status === 'accepted' || info.status === 'not_required') {
            setAdConsentStatus(info.status);
            setSettings((prev) => ({ ...prev, adPersonalization: true }));
            appendMonetizationEvent({
              category: 'consent',
              action: 'personalized_ads_enabled',
              status: 'success',
              detail: `Consent provider returned status ${info.status}.`
            });
            return;
          }
        }
      } catch {
        appendMonetizationEvent({ category: 'consent', action: 'consent_form', status: 'error', detail: 'Consent form request failed.' });
        setBillingStatusText(tr('options.ad_consent_sync_failed'));
      }
    }
    if (isAdConsentRequired() && adConsentStatus !== 'accepted') {
      setPendingPersonalizedAds(true);
      setShowAdConsent(true);
      appendMonetizationEvent({ category: 'consent', action: 'consent_prompt_shown', status: 'info', detail: 'App is waiting for manual ad consent confirmation.' });
      return;
    }
    setSettings((prev) => ({ ...prev, adPersonalization: true }));
    appendMonetizationEvent({ category: 'consent', action: 'personalized_ads_enabled', status: 'success', detail: 'Personalized ads enabled without extra consent requirement.' });
  }

  async function openAdPrivacyOptions(): Promise<void> {
    if (!adConsentProviderAvailable) {
      setBillingStatusText(tr('options.ad_privacy_options_unavailable'));
      return;
    }
    try {
      const opened = await openAdPrivacyOptionsForm();
      if (!opened) {
        appendMonetizationEvent({ category: 'consent', action: 'privacy_options', status: 'error', detail: 'Privacy options form was unavailable.' });
        setBillingStatusText(tr('options.ad_privacy_options_unavailable'));
        return;
      }
      const info = await refreshAdConsentProviderInfo();
      if (info) {
        setAdPrivacyOptionsAvailable(info.privacyOptionsRequired);
        if (info.status === 'accepted' || info.status === 'not_required') {
          setAdConsentStatus(info.status);
          if (info.status === 'accepted') {
            setSettings((prev) => ({ ...prev, adPersonalization: true }));
          }
        }
      }
      appendMonetizationEvent({ category: 'consent', action: 'privacy_options', status: 'success', detail: 'Opened ad privacy options and refreshed consent state.' });
      setBillingStatusText(tr('options.ad_privacy_options_opened'));
    } catch {
      appendMonetizationEvent({ category: 'consent', action: 'privacy_options', status: 'error', detail: 'Opening ad privacy options failed.' });
      setBillingStatusText(tr('options.ad_privacy_options_failed'));
    }
  }

  function completeOnboarding(): void {
    setShowOnboarding(false);
    localStorage.setItem(ONBOARDING_DONE_STORAGE_KEY, '1');
  }

  function completeLegalConsent(): void {
    localStorage.setItem(LEGAL_CONSENT_DONE_STORAGE_KEY, '1');
    setShowLegalConsent(false);
    const dataDisclosureDone = localStorage.getItem(DATA_DISCLOSURE_CONSENT_STORAGE_KEY) !== null;
    if (!dataDisclosureDone) {
      setShowDataDisclosure(true);
      return;
    }
    const onboardingDone = localStorage.getItem(ONBOARDING_DONE_STORAGE_KEY) === '1';
    if (!onboardingDone) setShowOnboarding(true);
  }

  function completeDataDisclosure(choices: {
    localDataAck: boolean;
    cloudBackupAck: boolean;
    billingAck: boolean;
    personalizedAds: boolean;
  }): void {
    const payload = {
      version: 1,
      acceptedAtMs: nowMs(),
      ...choices
    };
    localStorage.setItem(DATA_DISCLOSURE_CONSENT_STORAGE_KEY, JSON.stringify(payload));
    void updateAdPersonalization(choices.personalizedAds);
    setShowDataDisclosure(false);
    const onboardingDone = localStorage.getItem(ONBOARDING_DONE_STORAGE_KEY) === '1';
    if (!onboardingDone) setShowOnboarding(true);
  }

  function replayOnboarding(): void {
    setOnboardingStep(0);
    setShowOnboarding(true);
    localStorage.removeItem(ONBOARDING_DONE_STORAGE_KEY);
  }

  async function exitFromLegalConsent(): Promise<void> {
    try {
      const { App } = await import('@capacitor/app');
      await App.exitApp();
    } catch {
      window.close();
    }
  }

  function openLegalDoc(kind: 'privacy' | 'terms'): void {
    setLegalDoc(kind);
  }

  function closeLegalDoc(): void {
    setLegalDoc(null);
  }

  function withdrawAdConsent(): void {
    if (isAdConsentRequired()) setAdConsentStatus('rejected');
    if (!settings.adPersonalization) {
      appendMonetizationEvent({ category: 'consent', action: 'withdraw_consent', status: 'info', detail: 'Withdraw consent requested, but personalized ads were already off.' });
      setBillingStatusText(tr('options.ad_consent_already_off'));
      return;
    }
    setSettings((prev) => ({ ...prev, adPersonalization: false }));
    appendMonetizationEvent({ category: 'consent', action: 'withdraw_consent', status: 'success', detail: 'Personalized ads disabled and consent withdrawn.' });
    setBillingStatusText(tr('options.ad_consent_withdrawn'));
  }

  function allowPersonalizedAds(): void {
    setAdConsentStatus('accepted');
    setShowAdConsent(false);
    if (pendingPersonalizedAds) {
      setSettings((prev) => ({ ...prev, adPersonalization: true }));
    }
    setPendingPersonalizedAds(false);
    appendMonetizationEvent({ category: 'consent', action: 'modal_allow_personalized', status: 'success', detail: 'User allowed personalized ads from the consent modal.' });
  }

  function keepNonPersonalizedAds(): void {
    setAdConsentStatus(isAdConsentRequired() ? 'rejected' : 'not_required');
    setSettings((prev) => ({ ...prev, adPersonalization: false }));
    setPendingPersonalizedAds(false);
    setShowAdConsent(false);
    appendMonetizationEvent({ category: 'consent', action: 'modal_keep_non_personalized', status: 'info', detail: 'User kept non-personalized ads from the consent modal.' });
  }

  return {
    showOnboarding,
    updateAdPersonalization,
    openAdPrivacyOptions,
    completeOnboarding,
    completeLegalConsent,
    completeDataDisclosure,
    replayOnboarding,
    exitFromLegalConsent,
    openLegalDoc,
    closeLegalDoc,
    withdrawAdConsent,
    allowPersonalizedAds,
    keepNonPersonalizedAds
  };
}
